// src/pages/campaign-console/consoleData.js
// Shared config + helpers for the Launch Console. Pure data — no JSX.

import { MessageSquare, MessageCircle, Phone, Facebook, SlidersHorizontal, Sparkles, Repeat, BookOpen, ClipboardCheck } from 'lucide-react';

// The five setup steps == the five Campaign Settings tabs, in the same order.
// Completion mirrors the panel's `tabStatus` (see getSetup / consoleGetSetup),
// and each step opens that tab in the full settings panel.
export const STEPS = [
  { key: 'setup',         label: 'Setup',         Icon: SlidersHorizontal, help: 'Number, timezone, business hours & talk track' },
  { key: 'behavior',      label: 'Behavior',      Icon: Sparkles,          help: 'Tone & persona' },
  { key: 'followups',     label: 'Follow-ups',    Icon: Repeat,            help: 'When and how the AI follows up' },
  { key: 'knowledge',     label: 'Knowledge',     Icon: BookOpen,          help: 'What the AI can answer from' },
  { key: 'qualification', label: 'Qualification', Icon: ClipboardCheck,    help: 'Questions asked before handoff' },
];

export const CHANNEL = {
  sms:       { label: 'SMS',       Icon: MessageSquare },
  chat:      { label: 'Web Chat',  Icon: MessageCircle },
  voice:     { label: 'Voice',     Icon: Phone },
  messenger: { label: 'Messenger', Icon: Facebook },
};

// status -> visual tokens (Tailwind classes use the `surfox` palette you add
// to tailwind.config.js — see INTEGRATION.md).
export const STATUS = {
  live:   { label: 'Live',        dot: 'bg-surfox-cyan',  text: 'text-surfox-cyan',  soft: 'bg-[#e4f5f8]' },
  ready:  { label: 'Ready',       dot: 'bg-surfox-green', text: 'text-surfox-green', soft: 'bg-[#e7f5ee]' },
  setup:  { label: 'Needs setup', dot: 'bg-surfox-amber', text: 'text-surfox-amber', soft: 'bg-surfox-amberSoft' },
  paused: { label: 'Paused',      dot: 'bg-surfox-faint', text: 'text-surfox-muted', soft: 'bg-surfox-line' },
};

// Map a real campaign row -> which of the five tabs are complete.
// This default is a best-effort SMS fallback; the app passes a richer
// `consoleGetSetup` that mirrors the panel's `tabStatus` exactly (incl. web/voice
// exemptions, talk-track gating, and knowledge-asset checks).
export function getSetup(c) {
  return {
    setup:         !!(c.ai_representative_name && String(c.ai_representative_name).trim()) && !!c.phone_number_id && !!c.timezone,
    behavior:      !!(c.initial_tone && c.initial_persona && c.engagement_tone && c.engagement_persona),
    followups:     true,
    knowledge:     c.knowledge_linked !== undefined ? !!c.knowledge_linked : true,
    qualification: !c.require_qualification_before_transfer
                   || !!(Array.isArray(c.qualification_questions) && c.qualification_questions.some(q => q && q.trim())),
  };
}

// Industries that expose a dynamic talk-track field (mirrors the Settings panel).
export const DYNAMIC_TALK_TRACK_INDUSTRIES = [
  'Staffing', 'Real Estate', 'Home Services', 'Financial Services', 'Auto Sales', 'Mortgage Lending',
];

// Single source of truth for the 5-section readiness model, shared by the
// Campaigns page (consoleGetSetup) and the Get-Live Companion (useGetLiveStatus)
// so "green" means the same thing everywhere.
//
// Decision B (WIRING.md): the ring used to ignore two checks the AI-ON gate
// (validateAiToggle) enforces, so 5/5 could still fail Go-live. We fold both in:
//   • Behavior also requires `ai_instruction_initial` (Behavior is what generates it).
//   • Setup also requires business hours configured (tenant-level platform_settings).
//   • Setup also requires `notification_email` (2026-08-28). It was required by
//     NEITHER the ring nor the gate, so a campaign could go 5/5, pass activation
//     and text real leads with nowhere for its hot-lead alerts to go. Added to
//     both in the same change, because adding it to the gate alone is exactly the
//     "5/5 still fails Go-live" defect this block exists to prevent.
//   • Web chat (2026-09-10) REQUIRES a talk track and a hot-lead email, and does NOT
//     require the Behavior fields, ai_instruction_initial or qualification questions,
//     because generate-web-chat-reply reads the first and never the rest. Same rules
//     in validateAiToggle and the ai-toggle route. Voice and SMS are unchanged.
// Result: 5/5 ⇒ validateAiToggle passes (modulo the live SMS-limit check, which
// can't be known statically). Decision A still holds: a fresh campaign reads 2/5
// (Follow-ups + Qualification green; Setup/Behavior/Knowledge red).
//
// `ctx` carries the signals that don't live on the campaign row:
//   { widgetCampaignId, voiceCampaignId, isGlobalAdmin, industries,
//     knowledgeAssets, selectedKnowledgeAssets, businessHoursByTenant }
export function deriveCampaignSetup(c, ctx = {}) {
  const {
    widgetCampaignId, voiceCampaignId, messengerCampaignId, isGlobalAdmin = false,
    industries = {}, knowledgeAssets = [], selectedKnowledgeAssets = {},
    businessHoursByTenant = {}, isReviewer = false,
  } = ctx;

  // Review accounts and prospect demos (tenants.is_reviewer): every campaign reads as set up. They can never have a phone
  // number, business hours or an alert email, and cannot edit anything, so "Needs setup 3/5" was only a dead end beside a
  // dashboard that says the campaigns are active (first outside reviewer, 2026-10-03). Display only: nothing is written, and
  // the server-side go-live gate (validateAiToggle) is untouched, so this cannot turn a real campaign on. Keep the panel's
  // own tabStatus (CampaignDetailPanel in campaignManagement.jsx) in step, or the card and its tabs disagree.
  if (isReviewer) return { setup: true, behavior: true, followups: true, knowledge: true, qualification: true };

  const isWeb = c.id === widgetCampaignId;
  const isVoice = c.id === voiceCampaignId;
  // Messenger uses the SAME reply brain as web (generate-web-chat-reply with
  // channel:'messenger'; see supabase/functions/handle-messenger-message), so everywhere
  // web is exempt from an SMS-only requirement, Messenger is exempt the same way.
  const isMessenger = c.id === messengerCampaignId;
  const derived = isWeb || isVoice || isMessenger; // web-chat / voice / messenger
  const industry = industries[c.tenant_id] || '';
  const hasDynamicField = isGlobalAdmin || DYNAMIC_TALK_TRACK_INDUSTRIES.includes(industry);
  const hasTalkTrack = !!(c.talk_track_type || c.custom_talk_track_id);
  // Web chat builds its whole brain from the campaign's talk track
  // (generate-web-chat-reply: strategy_block + pillar_data + objectives_block), so web
  // REQUIRES one, in every industry, exactly as validateAiToggle and the ai-toggle route
  // do. Voice stays exempt. SMS is unchanged.
  const talkTrackGreen = (isWeb || isMessenger) ? hasTalkTrack : (isVoice || !hasDynamicField || hasTalkTrack);
  const hasMasterDoc = knowledgeAssets?.some(a => a.tenant_id === c.tenant_id && a.is_master);
  const hasLinkedDoc = (selectedKnowledgeAssets?.[c.id]?.length || 0) > 0;
  // Derived campaigns (web/voice) are 24/7 — exempt from phone/timezone/hours.
  const hoursConfigured = derived || !!businessHoursByTenant[c.tenant_id];

  // Where a hot lead actually gets sent. Required on EVERY channel, web and voice
  // included: those produce hot leads too, and _shared/notificationRecipient.ts
  // puts this address in the To and only ADDS the tenant owner to the cc. Null
  // here means the To silently collapses to the tenant address and the campaign's
  // own assignee is never told - the same failure fixed in detect-dropped-handoffs
  // on 2026-08-20, arriving instead through an address that was never set.
  const alertEmailSet = !!(c.notification_email && String(c.notification_email).trim());

  return {
    setup: (derived ? true : !!(c.ai_representative_name && c.ai_representative_name.trim()))
           && (derived || !!c.phone_number_id)
           && (derived || !!c.timezone)
           && talkTrackGreen
           && hoursConfigured
           && alertEmailSet,
    // Web: generate-web-chat-reply never reads the four tone/persona fields,
    // ai_instruction_initial, require_qualification_before_transfer or
    // qualification_questions (its campaign select has none of them), and the settings
    // panel replaces those two tabs with one line for web. Requiring them would block a
    // web campaign on fields that change nothing. validateAiToggle skips the same ones
    // for web, so 5/5 still means Go live passes.
    behavior: (isWeb || isMessenger) ? true
              : !!(c.initial_tone && c.initial_persona && c.engagement_tone && c.engagement_persona)
                && !!(c.ai_instruction_initial && String(c.ai_instruction_initial).trim()),
    // Follow-ups have safe defaults and never block launch.
    followups: true,
    knowledge: !!hasMasterDoc || hasLinkedDoc,
    qualification: (isWeb || isMessenger) ? true
                   : !c.require_qualification_before_transfer
                     || !!(Array.isArray(c.qualification_questions) && c.qualification_questions.some(q => q && q.trim())),
  };
}

export const stepsDone = (setup) => Object.values(setup).filter(Boolean).length;

// Derive a status when the row doesn't carry one explicitly.
export function deriveStatus(c, allDone) {
  if (c.ai_on) return 'live';
  if (c.is_active === false && allDone) return 'paused';
  return allDone ? 'ready' : 'setup';
}

export const fmtNum = (v) => Number(v || 0).toLocaleString('en-US');
export const fmtPct = (v) => Math.round((v || 0) * 100) + '%';
